import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Sparkles,
  AlertOctagon,
  Building2,
  TrendingUp,
  CalendarDays,
  Users,
  AlertTriangle,
  Activity,
  ChevronRight
} from 'lucide-react';
import PageMeta from '../../shared/PageMeta';
import {
  dashboardService,
  type SuperAdminBucket,
  type SuperAdminProjectDetail
} from '../../services/dashboardService';
import HealthScoreGauge from './superAdmin/HealthScoreGauge';
import ComplianceHeatmap from './superAdmin/ComplianceHeatmap';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'reporting', label: 'Daily Reporting' },
  { id: 'tasks', label: 'Tasks & Issues' },
  { id: 'team', label: 'Team' }
] as const;

type TabId = typeof TABS[number]['id'];

const BUCKET_PILL: Record<SuperAdminBucket, string> = {
  completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20',
  readyToStart: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 ring-blue-500/20',
  onTrack: 'bg-teal-500/10 text-teal-600 dark:text-teal-400 ring-teal-500/20',
  atRisk: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20',
  draftIncomplete: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 ring-slate-500/20'
};

const SEVERITY_COLOR: Record<'low' | 'medium' | 'high' | 'critical', string> = {
  low: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
  medium: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  high: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  critical: 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
};

const ENG_STATUS_PILL: Record<'active' | 'idle' | 'inactive', string> = {
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  idle: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  inactive: 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
};

function SCurveChart({ series }: { series: SuperAdminProjectDetail['sCurve'] }) {
  if (!series.length) return null;
  const w = 600;
  const h = 200;
  const padding = { top: 10, right: 10, bottom: 24, left: 30 };
  const innerW = w - padding.left - padding.right;
  const innerH = h - padding.top - padding.bottom;
  const stepX = innerW / Math.max(series.length - 1, 1);

  const buildPath = (key: 'planned' | 'actual') =>
    series.map((p, i) => {
      const x = padding.left + i * stepX;
      const y = padding.top + innerH - (p[key] / 100) * innerH;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-48 w-full min-w-[480px]">
        {[0, 25, 50, 75, 100].map(g => {
          const y = padding.top + innerH - (g / 100) * innerH;
          return (
            <g key={g}>
              <line x1={padding.left} x2={w - padding.right} y1={y} y2={y} className="stroke-slate-200 dark:stroke-slate-700" strokeDasharray="2 4" strokeWidth={0.5} />
              <text x={padding.left - 6} y={y + 3} textAnchor="end" className="fill-slate-400 text-[9px]">{g}%</text>
            </g>
          );
        })}
        <path d={buildPath('planned')} fill="none" className="stroke-slate-400" strokeWidth={1.5} strokeDasharray="4 4" />
        <path d={buildPath('actual')} fill="none" className="stroke-violet-500" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
        {series.length > 1 && (() => {
          const last = series[series.length - 1]!;
          const x = padding.left + (series.length - 1) * stepX;
          const y = padding.top + innerH - (last.actual / 100) * innerH;
          return <circle cx={x} cy={y} r={4} className="fill-violet-500" />;
        })()}
      </svg>
      <div className="mt-1 flex items-center justify-end gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1">
          <span className="block h-0.5 w-4 border-t-2 border-dashed border-slate-400" />
          Planned
        </span>
        <span className="flex items-center gap-1">
          <span className="block h-0.5 w-4 bg-violet-500" />
          Actual
        </span>
      </div>
    </div>
  );
}

export default function SuperAdminProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('summary');
  const [data, setData] = useState<SuperAdminProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await dashboardService.getSuperAdminProjectDetail(projectId);
        setData(res);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load project';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const breakdownTiles = useMemo(() => {
    if (!data) return [];
    const t = data.taskBreakdown;
    return [
      { label: 'Total', value: t.total, color: 'text-slate-900 dark:text-white' },
      { label: 'Completed', value: t.completed, color: 'text-emerald-600 dark:text-emerald-400' },
      { label: 'In Progress', value: t.inProgress, color: 'text-blue-600 dark:text-blue-400' },
      { label: 'Pending', value: t.pending, color: 'text-amber-600 dark:text-amber-400' },
      { label: 'Overdue', value: t.overdue, color: 'text-orange-600 dark:text-orange-400' },
      { label: 'Failed', value: t.failed, color: 'text-rose-600 dark:text-rose-400' }
    ];
  }, [data]);

  if (loading) {
    return <div className="flex h-[60vh] items-center justify-center"><Sparkles className="h-6 w-6 animate-pulse text-violet-500" /></div>;
  }

  if (error || !data) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-2 text-rose-600">
        <AlertOctagon className="h-6 w-6" />
        {error ?? 'Project not found'}
      </div>
    );
  }

  return (
    <>
      <PageMeta title={`${data.project.projectName} · Deep Dive`} description="Project deep-dive" />

      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-violet-600 dark:text-slate-300 dark:hover:text-violet-400"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ring-1 ${BUCKET_PILL[data.project.bucket]}`}>
            {data.project.bucket}
          </span>
        </div>

        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-violet-500/5 via-white to-blue-500/5 p-5 shadow-sm dark:border-slate-700/50 dark:from-violet-900/10 dark:via-slate-800/60 dark:to-blue-900/10"
        >
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 text-white">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">
                  {data.project.code} · {data.project.department}
                </p>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{data.project.projectName}</h1>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {data.project.customer} · {data.project.location || 'No location'} · Manager: {data.project.manager}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700 dark:bg-slate-700/40 dark:text-slate-300">
                    Priority: {data.project.priority}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700 dark:bg-slate-700/40 dark:text-slate-300">
                    Progress: {data.project.progress}%
                  </span>
                </div>
              </div>
            </div>
            <HealthScoreGauge score={data.project.healthScore} />
          </div>

          {/* Task breakdown tiles */}
          <div className="mt-5 grid grid-cols-3 gap-2 lg:grid-cols-6">
            {breakdownTiles.map(t => (
              <div key={t.label} className="rounded-xl bg-white px-3 py-2 text-center shadow-sm dark:bg-slate-800/60">
                <p className={`text-xl font-bold tabular-nums ${t.color}`}>{t.value}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* TAB BAR */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`relative px-4 py-2 text-xs font-semibold transition ${
                tab === t.id
                  ? 'text-violet-600 dark:text-violet-400'
                  : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
              }`}
            >
              {t.label}
              {tab === t.id && (
                <motion.span
                  layoutId="superadmin-tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-500"
                />
              )}
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        {tab === 'summary' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60 lg:col-span-2">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/10">
                  <TrendingUp className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Planned vs Actual (S-curve)</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Cumulative completion % over last 30 days</p>
                </div>
              </div>
              <SCurveChart series={data.sCurve} />
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10">
                  <CalendarDays className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Timeline</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Key dates</p>
                </div>
              </div>
              <ul className="space-y-2 text-xs">
                <li className="flex items-center justify-between"><span className="text-slate-500">Planned start</span><span className="font-semibold text-slate-900 dark:text-white">{data.project.plannedStart ? new Date(data.project.plannedStart).toLocaleDateString() : '—'}</span></li>
                <li className="flex items-center justify-between"><span className="text-slate-500">Planned end</span><span className="font-semibold text-slate-900 dark:text-white">{data.project.plannedEnd ? new Date(data.project.plannedEnd).toLocaleDateString() : '—'}</span></li>
                <li className="flex items-center justify-between"><span className="text-slate-500">Actual start</span><span className="font-semibold text-slate-900 dark:text-white">{data.project.actualStart ? new Date(data.project.actualStart).toLocaleDateString() : '—'}</span></li>
                <li className="flex items-center justify-between"><span className="text-slate-500">Status</span><span className="font-semibold text-slate-900 dark:text-white capitalize">{data.project.status}</span></li>
              </ul>
            </div>
          </div>
        )}

        {tab === 'reporting' && (
          <ComplianceHeatmap
            dates={data.complianceHeatmap.dates}
            rows={[{ projectId: data.project.projectId, projectName: data.project.projectName, values: data.complianceHeatmap.values }]}
            title="30-day Reporting Compliance"
            subtitle="Each cell = a day · click cell to view submissions"
          />
        )}

        {tab === 'tasks' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-500/10">
                  <AlertTriangle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Open Issues</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Overdue or failed tasks</p>
                </div>
              </div>
              {data.issues.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-slate-400">No open issues — well done</div>
              ) : (
                <ul className="space-y-2">
                  {data.issues.map(iss => (
                    <li key={iss.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200/80 bg-slate-50/40 px-3 py-2 dark:border-slate-700/50 dark:bg-slate-900/30">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{iss.title}</p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">{iss.owner} · {iss.ageDays}d old</p>
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SEVERITY_COLOR[iss.severity]}`}>{iss.severity}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10">
                  <Activity className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Recent Activity</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Last 7 days</p>
                </div>
              </div>
              {data.recentActivity.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-slate-400">No recent activity</div>
              ) : (
                <ul className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                  {data.recentActivity.map(a => (
                    <li key={a.id} className="rounded-xl border border-transparent bg-slate-50/40 px-3 py-2 dark:bg-slate-900/30">
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{a.message}</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">{a.actor} · {a.relativeTime}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'team' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/10">
                  <Users className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Leadership</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <TeamRow label="Manager" person={data.team.manager} navigate={navigate} />
                <TeamRow label="Head" person={data.team.head} navigate={navigate} />
                {data.team.supervisors.map(s => (
                  <TeamRowSupervisor key={s.userId} supervisor={s} navigate={navigate} />
                ))}
                {data.team.supervisors.length === 0 && (
                  <li className="text-xs text-slate-400">No supervisors assigned</li>
                )}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60 lg:col-span-2">
              <div className="mb-4 flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10">
                  <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Site Engineers</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Click to open performance view</p>
                </div>
              </div>
              {data.team.engineers.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-slate-400">No engineers assigned yet</div>
              ) : (
                <ul className="space-y-2">
                  {data.team.engineers.map(e => (
                    <li key={e.userId}>
                      <button
                        onClick={() => navigate(`/dashboard/super-admin/users/${e.userId}`)}
                        className="group flex w-full items-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50/40 px-3 py-2 transition hover:border-violet-300 hover:bg-white dark:border-slate-700/50 dark:bg-slate-900/30"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-blue-400 text-sm font-semibold text-white">
                          {e.name.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{e.name}</p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400">
                            {e.uploadsLast7d} uploads · {e.passRate}% pass rate
                          </p>
                        </div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ENG_STATUS_PILL[e.status]}`}>{e.status}</span>
                        <span className="text-xs font-bold tabular-nums text-slate-900 dark:text-white">{e.score}</span>
                        <ChevronRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-violet-500" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function TeamRow({
  label,
  person,
  navigate
}: {
  label: string;
  person: { userId: string; name: string } | null;
  navigate: ReturnType<typeof useNavigate>;
}) {
  if (!person) return <li className="flex items-center justify-between"><span className="text-xs text-slate-500">{label}</span><span className="text-xs text-slate-400">—</span></li>;
  return (
    <li>
      <button
        onClick={() => navigate(`/dashboard/super-admin/users/${person.userId}`)}
        className="flex w-full items-center justify-between rounded-xl px-2 py-1.5 transition hover:bg-slate-50 dark:hover:bg-slate-700/40"
      >
        <span className="text-xs text-slate-500">{label}</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-white">
          {person.name}<ChevronRight className="h-4 w-4 text-slate-400" />
        </span>
      </button>
    </li>
  );
}

function TeamRowSupervisor({
  supervisor,
  navigate
}: {
  supervisor: { userId: string; name: string; score: number };
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <li>
      <button
        onClick={() => navigate(`/dashboard/super-admin/users/${supervisor.userId}`)}
        className="flex w-full items-center justify-between rounded-xl px-2 py-1.5 transition hover:bg-slate-50 dark:hover:bg-slate-700/40"
      >
        <span className="text-xs text-slate-500">Supervisor</span>
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          {supervisor.name}
          <span className="text-[10px] tabular-nums text-slate-500">{supervisor.score}/100</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </span>
      </button>
    </li>
  );
}
